import { useQuery } from '@apollo/client'
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  GET_CART,
  PromotionSelectionCriteria,
  screenPath,
  cartLineItemsOf,
  toCartLineInputs,
  toCartRowModel,
  toCheckoutSummary,
  toDeferredPromotionModels,
  type DeferredPromotionModel,
  type DeferredPromotionOption,
  type IGetCart,
  type IGetCartVariables,
} from '@commerce/core'
import { YALO_PALETTE, YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { CartRow } from '../components/CartRow'
import { DeferredPromotions } from '../components/DeferredPromotions'
import { ActionBar, LabelledRow, Screen, TopBar } from '../design'
import { shippingMethods, useDemo } from '../demo'
import { useAppSelector } from '../hooks/useAppSelector'
import { useCounterRules } from '../hooks/useCounterRules'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { useActivateDeferredPromotion } from '../hooks/useActivateDeferredPromotion'
import { useRemoveFromCart } from '../hooks/useRemoveFromCart'
import { useDebouncedCartQuantity } from '../hooks/useDebouncedCartQuantity'

/**
 * Built once at the default express fee, which is the design's own
 * `expressFee ?? 60`. There is no shipping endpoint to ask.
 */
const SHIPPING_METHODS = shippingMethods()

/**
 * The cart.
 *
 * The lines, the quantities and the totals are the real cart over GraphQL; the
 * shipping fee is not. `toCheckoutSummary` keeps those apart — `cartTotal` is
 * the backend's, `shipping` is ours — so the breakdown shows the seam instead of
 * hiding it behind one number.
 *
 * The coupon field that used to sit above the breakdown is gone. It matched one
 * hard-coded code and took 10% off client-side; commerce-scs has no coupon
 * endpoint and neither does cinnamon, so the discount was money on screen that
 * no server would have honoured at checkout.
 */
export const CartPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const rules = useCounterRules()
  const presentation = usePresentationContext()
  const promotionConfig = useAppSelector((state) => state.config.config)
  const { shippingMethodId, distributor } = useDemo()
  // Debounced: a burst of stepper taps on a row collapses into one `replace`
  // mutation with the final quantity instead of one round trip per tap. The
  // row's stepper shows its own local count meanwhile, so no overlay is needed.
  const { changeQuantity, error: updateError } = useDebouncedCartQuantity()
  const { removeItem, error: removeError } = useRemoveFromCart()
  const { activate, activatingUids, error: activateError } = useActivateDeferredPromotion()

  const { data, loading, error } = useQuery<IGetCart, IGetCartVariables>(GET_CART, {
    variables: { cartStorefrontName: slug, cartSessionUid: sessionId },
  })

  const cart = data?.cart

  // Two shapes of the same lines: `toCheckoutSummary` totals the raw ones, the
  // rows render the resolved ones. Mapping twice from one source beats deriving
  // one from the other and having to un-format a price to add it up.
  const lines = useMemo(
    () =>
      toCartLineInputs({
        // Some storefronts return the lines grouped under `cart.groups`, not
        // flat in `cart.items`; this reads whichever shape came back.
        items: cartLineItemsOf(cart),
        activePromotions: cart?.activePromotions,
        config: promotionConfig,
      }),
    [cart, promotionConfig],
  )

  const models = useMemo(
    () => lines.map((line) => toCartRowModel(line, presentation)),
    [lines, presentation],
  )

  /**
   * The promotions the basket has already earned but not claimed. Mapped from
   * the same query result as the lines: activating one answers with the whole
   * cart, so this list shrinks — and the totals move — off one repaint.
   */
  const deferredPromotions = useMemo(
    () => toDeferredPromotionModels(cart?.deferredPromotions),
    [cart],
  )

  const claimPromotion = useCallback(
    (model: DeferredPromotionModel, option?: DeferredPromotionOption) => {
      void activate({
        promotionUid: model.promotionUid,
        selectBy: model.selectBy,
        ...(model.selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION && option
          ? { selectedGroupIdentifier: option.identifier }
          : {}),
      })
    },
    [activate],
  )

  /**
   * `cartTotal` is `cart.total`, not `cart.subtotal`: the total is what the
   * backend stands behind, with every promotion and fee it applied already in
   * it. The payment screen passes the same number, so the total cannot move
   * between here and checkout.
   */
  const summary = useMemo(
    () =>
      toCheckoutSummary(
        {
          lines,
          cartTotal: cart?.total ?? 0,
          shippingFee: SHIPPING_METHODS.find((method) => method.id === shippingMethodId)?.fee ?? 0,
          creditAvailable: distributor.credit,
        },
        presentation,
      ),
    [lines, cart, shippingMethodId, distributor, presentation],
  )

  // The shipping fee is not the backend's, so charging it makes the total ours.
  const isEstimate = summary.shipping > 0

  const catalogPath = screenPath('catalog', { slug, sessionId })

  if (loading) {
    return (
      <Screen testId="cart-page">
        <TopBar title="Mi carrito" backTo={catalogPath} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Cargando carrito" />
        </Box>
      </Screen>
    )
  }

  if (error) {
    return (
      <Screen testId="cart-page">
        <TopBar title="Mi carrito" backTo={catalogPath} />
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      </Screen>
    )
  }

  const writeError = updateError ?? removeError ?? activateError

  return (
    <Screen testId="cart-page">
      <TopBar title="Mi carrito" backTo={catalogPath} />

      {/* A failed write leaves the row showing its old quantity and nothing
          else changes, so without this the shopper sees no reason why. */}
      {writeError && (
        <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
          {writeError.message}
        </Alert>
      )}

      {models.length === 0 ? (
        <Stack spacing={1.5} alignItems="center" sx={{ px: 5, py: 8, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ color: YALO_SEMANTIC.fg4 }}>
            0
          </Typography>
          <Typography fontWeight={600}>Tu carrito está vacío.</Typography>
          <Button
            component={RouterLink}
            to={catalogPath}
            sx={{ color: 'primary.dark' }}
          >
            Explorar productos
          </Button>
        </Stack>
      ) : (
        <>
          <Stack spacing={1.5} sx={{ flex: 1, px: 2, py: 2 }}>
            {models.map((model) => (
              <CartRow
                key={model.lineKey}
                model={model}
                rules={rules}
                onQuantityChange={changeQuantity}
                onRemove={removeItem}
              />
            ))}

            {/* Above the totals, not below: the reward changes the number the
                shopper is about to read, so it has to be seen first. */}
            <DeferredPromotions
              models={deferredPromotions}
              activatingUids={activatingUids}
              onClaim={claimPromotion}
            />

            <Stack
              spacing={1}
              sx={{
                bgcolor: YALO_SEMANTIC.bgSurface,
                border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
                borderRadius: `${YALO_RADIUS.lg}px`,
                p: 1.75,
              }}
            >
              {/* The design labels this "Subtotal", but the value is the
                  backend's `cart.total` — promotions and fees included — and
                  calling it a subtotal would misname the one number on this
                  screen the server actually stands behind. */}
              <LabelledRow
                label={`Total del carrito · ${summary.itemCount} ${summary.itemCount === 1 ? 'artículo' : 'artículos'}`}
                value={summary.cartTotalText}
              />

              <LabelledRow
                label="Envío"
                value={summary.shipping > 0 ? summary.shippingText : 'Gratis'}
              />

              <Divider />

              <LabelledRow
                label={isEstimate ? 'Total estimado' : 'Total'}
                value={summary.totalText}
                strong
              />

              {/* The seam, said out loud. The breakdown already shows it — the
                  first row is the backend's number and the two below it are
                  ours — but only someone who knows that reads it that way. */}
              {isEstimate && (
                <Typography variant="caption" color="text.secondary">
                  El descuento y el envío son de esta demostración: commerce-scs no tiene endpoint
                  para ninguno de los dos. La primera línea es el total que confirma el servidor.
                </Typography>
              )}
            </Stack>
          </Stack>

          <ActionBar>
            <Button
              component={RouterLink}
              to={screenPath('delivery', { slug, sessionId })}
              fullWidth
              size="large"
              variant="contained"
              sx={{
                justifyContent: 'space-between',
                bgcolor: YALO_PALETTE.green,
                color: YALO_PALETTE.neutral100,
                '&:hover': { bgcolor: YALO_PALETTE.green, filter: 'brightness(1.05)' },
              }}
            >
              <span>Realizar pedido</span>
              {/* A storefront that hides prices would otherwise show a pill
                  with a gap where the total should be. */}
              {!summary.pricesHidden && <span>{summary.totalText}</span>}
            </Button>
          </ActionBar>
        </>
      )}
    </Screen>
  )
}
